/**
 * PREPARE FOR SEA — and READINESS THAT IS MEASURED, NOT REMEMBERED.
 *
 * The tempting implementation is a flag: download everything, write `prepared: true`, show a green
 * tick. That flag is a CLAIM, and it goes wrong in exactly the situation it exists for — the browser
 * evicts a cache under storage pressure, a deploy changes a URL, a download half-finished on a
 * failing wifi in port. The flag still says ready. The cadet finds out at sea.
 *
 * So nothing here is remembered. Readiness is COMPUTED, every time it is asked for, by interrogating
 * the actual Cache API for the actual URLs the server says make up an offline Academy. If the answer
 * is "you have 91 of 97", that is a fact about the storage on this device at this moment, not a note
 * somebody wrote earlier. The same discipline as the rest of the Academy, turned on itself.
 *
 * WHY THE SERVICE WORKER'S CACHE AND NOT IndexedDB. These are HTTP responses to URLs the app already
 * fetches. Put them in the worker's cache and the app needs no offline code path at all: the same
 * fetch that works in port is served from cache at sea, by the worker, with no branch anywhere. A
 * parallel IndexedDB store would mean a second retrieval path in every widget, and two paths means
 * one of them is the one nobody tested.
 */

const PLAN_URL = '/api/offline/plan';

/** The plan itself is cached like everything else, so this answers at sea too. */
export async function offlinePlan() {
  const r = await fetch(PLAN_URL);
  if (!r.ok) throw new Error(`the plan is unavailable (HTTP ${r.status})`);
  return r.json();
}

/** Every URL in the plan, flattened, plan URL included — it must survive offline as well. */
export function planUrls(plan) {
  return [PLAN_URL, ...(plan?.groups || []).flatMap((g) => g.urls || [])];
}

/**
 * Fetch everything once so the worker caches it. `cache: 'reload'` bypasses the HTTP cache so this
 * is a real download rather than a revalidation that leaves the worker's cache untouched.
 *
 * Bytes are COUNTED, never predicted. The server deliberately promises no sizes: weighing a payload
 * means generating it, and any other figure would be an estimate shown to someone deciding whether
 * he has the bandwidth for it.
 *
 * WHAT THE BYTE COUNT IS, EXACTLY. `arrayBuffer().byteLength` is the DECODED size — what the cache
 * holds on the device. Responses arrive gzipped, so the amount that crossed the wire is several
 * times smaller and is not knowable from here (fetch does not expose transfer size). It is therefore
 * reported as STORED, never as downloaded. Naming a decoded figure "received" would overstate a
 * satellite bill by a factor of four, and the direction of an error does not make it true.
 */
export async function prepareForSea(plan, { onProgress, concurrency = 6 } = {}) {
  const urls = planUrls(plan);
  const failures = [];
  let done = 0, bytes = 0;
  let next = 0;

  const worker = async () => {
    while (next < urls.length) {
      const u = urls[next++];
      try {
        const r = await fetch(u, { cache: 'reload' });
        if (!r.ok) throw new Error(`HTTP ${r.status}`);
        /* AWAIT FIRST, THEN ADD. `bytes += await r.arrayBuffer()...` is a lost-update race, and it
           is a race in single-threaded JavaScript, which is why it survives review: a compound
           assignment evaluates its LEFT side before the await, so the sum is read, the worker
           yields, five other workers add to `bytes`, and this one writes back a stale base over the
           top of them. Six workers lost about a third of the total — 2.41 MB against a true 3.30 —
           and the loss VARIED per run (1.81, 1.85, 2.22, 2.41), while every URL measured perfectly
           on its own. Right in every test small enough to check by hand is the worst shape a wrong
           number can have. With concurrency 1 it read exactly 3.30 MB, which is what named it. */
        const len = (await r.arrayBuffer()).byteLength;
        bytes += len;
      } catch (err) {
        failures.push({ url: u, why: String(err?.message || err) });
      }
      done++;
      onProgress?.({ done, total: urls.length, bytes, failures: failures.length });
    }
  };
  await Promise.all(Array.from({ length: concurrency }, worker));

  /* A partial download is reported as partial. Rounding it up to success is the single most
     dangerous thing this file could do. */
  return { total: urls.length, stored: urls.length - failures.length, bytes, failures };
}

/**
 * What is ACTUALLY on this device right now. Asks the cache, per URL, per group.
 * Returns `null` for `supported` where the browser has no Cache API — an unknown, not a zero.
 */
export async function seaReadiness(plan) {
  if (typeof caches === 'undefined') {
    return { supported: false, groups: [], have: 0, want: 0,
             why: 'this browser has no cache storage — the Academy needs a signal to open' };
  }
  const groups = [];
  let have = 0, want = 0;

  for (const g of (plan?.groups || [])) {
    const present = [];
    const missing = [];
    for (const u of (g.urls || [])) {
      // eslint-disable-next-line no-await-in-loop
      const hit = await caches.match(u);
      (hit ? present : missing).push(u);
    }
    have += present.length; want += g.urls.length;
    groups.push({ id: g.id, label: g.label, caveat: g.caveat,
                  have: present.length, want: g.urls.length, missing: missing.slice(0, 4) });
  }

  /* The shell is asked for separately: without it the app cannot OPEN, which is a different and
     larger failure than a missing practice paper. */
  const shell = Boolean(await caches.match('/study/'));
  return { supported: true, shell, groups, have, want,
           ready: shell && have === want, partial: shell && have > 0 && have < want };
}

globalThis.__academyPrepare = { offlinePlan, prepareForSea, seaReadiness };
